import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type TrialBalanceEmptyProps = {
    /** The date the report was asked for, already formatted `DD/MM/YYYY`. */
    asOfText: string;
};

/**
 * No account had a balance on this date.
 *
 * This is a **real answer**, not a failure and not an unknown: the seeded ledger
 * gives it for any date before its first posting. It is rendered as words rather
 * than as a table of zeros under a balanced verdict, because a page of zeros that
 * balances perfectly is the most convincing wrong answer this screen can give —
 * and the reader has no way to tell it apart from a report that failed to load.
 *
 * The load failure is handled elsewhere and differently: it throws to the page's
 * `ErrorBoundary`, which offers a retry. Nothing renders zeros.
 */
export const TrialBalanceEmpty: React.FC<TrialBalanceEmptyProps> = ({
    asOfText,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-12 text-center"
            {...getNodeTestIdProp('trial-balance-empty')}
        >
            <Icon
                aria-hidden
                className="text-2xl text-muted"
                icon="gravity-ui:circle-info"
            />
            <span className="text-base font-semibold text-foreground">
                {t('trialBalance.empty.title', { date: asOfText })}
            </span>
            <span className="max-w-prose text-sm text-muted">
                {t('trialBalance.empty.message')}
            </span>
        </div>
    );
};
